import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { serviceApi } from '../../services/api';
import { toast } from 'react-toastify';
import confirmDialog from '../../components/ConfirmDialog';
import { formatTimestamp } from '../../utils/dateUtils';
import { 
  Card, Button, Table, Modal, Form, Spinner, 
  Badge, Tabs, Tab, Alert, ListGroup, Accordion
} from 'react-bootstrap';

const Services = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['services', 'common']);
  
  // Services state
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Service form state
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    name: '',
    endpoint: '',
    apikey: '',
    auth_method: 'bearer',
    auth_header_name: '',
    auth_param_name: '',
    response_format: 'json',
    user_fields: {
      username: '',
      email: ''
    }
  });
  const [savingService, setSavingService] = useState(false);
  const [serviceFormError, setServiceFormError] = useState(null);
  
  // Import job state
  const [importJobs, setImportJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [runningImport, setRunningImport] = useState(false);
  
  // User matching state
  const [showMatchingModal, setShowMatchingModal] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [resolvingConflict, setResolvingConflict] = useState(false);
  
  // Schedule state
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    interval_seconds: 86400, // Default to daily
    start_time: null
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('services');

  useEffect(() => {
    // Check if user has permission to access services
    if (!hasPermission('services', 'view')) {
      navigate('/unauthorized');
      return;
    }

    // Fetch services
    fetchServices();
    
    // Fetch import jobs
    fetchImportJobs();
  }, [hasPermission, navigate]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await serviceApi.getAllServices();
      setServices(response.data);
      setError(null);
    } catch (err) {
      setError(t('services:loadFailed'));
      console.error('Error fetching services:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchImportJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await serviceApi.getAllImportJobs();
      setImportJobs(response.data);
    } catch (err) {
      console.error('Error fetching import jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleCreateService = () => {
    setEditingService(null);
    setServiceForm({
      name: '',
      endpoint: '',
      apikey: '',
      auth_method: 'bearer',
      auth_header_name: '',
      auth_param_name: '',
      response_format: 'json',
      user_fields: {
        username: '',
        email: ''
      }
    });
    setShowServiceModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      endpoint: service.endpoint,
      apikey: service.apikey || '',
      auth_method: service.auth_method || 'bearer',
      auth_header_name: service.auth_header_name || '',
      auth_param_name: service.auth_param_name || '',
      response_format: service.response_format || 'json',
      user_fields: service.user_fields
    });
    setShowServiceModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    confirmDialog({
      message: t('services:confirmDelete'),
      onConfirm: async () => {
        try {
          await serviceApi.deleteService(serviceId);
          setServices(services.filter(service => service.id !== serviceId));
          toast.success(t('services:deleteSuccess'));
        } catch (err) {
          toast.error(t('services:deleteFailed'));
          console.error('Error deleting service:', err);
        }
      }
    });
  };

  const handleServiceFormChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('user_fields.')) {
      const fieldName = name.split('.')[1];
      setServiceForm(prev => ({
        ...prev,
        user_fields: {
          ...prev.user_fields,
          [fieldName]: value
        }
      }));
    } else {
      setServiceForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleServiceFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSavingService(true);
      setServiceFormError(null);
      
      if (editingService) {
        // Update existing service
        const response = await serviceApi.updateService(editingService.id, serviceForm);
        setServices(services.map(service => 
          service.id === editingService.id ? response.data : service
        ));
      } else {
        // Create new service
        const response = await serviceApi.createService(serviceForm);
        setServices([...services, response.data]);
      }
      
      setShowServiceModal(false);
    } catch (err) {
      setServiceFormError(err.response?.data?.detail || 'Failed to save service. Please try again.');
      console.error('Error saving service:', err);
    } finally {
      setSavingService(false);
    }
  };

  const handleRunImport = async (serviceId, runType) => {
    try {
      setRunningImport(true);
      
      const response = await serviceApi.createImportJob(serviceId, runType);
      
      // Add the new job to the list
      setImportJobs([response.data, ...importJobs]);
      
      // If this is a dry-run, show the job details
      if (runType === 'dry-run') {
        setSelectedJob(response.data);
        setShowJobModal(true);
      }
      
      // If this is a run and there are conflicts, show the matching modal
      if (runType === 'run' && response.data.status === 'needs_review') {
        await fetchJobDetails(response.data.id);
      }
    } catch (err) {
      setError('Failed to start import. Please try again.');
      console.error('Error running import:', err);
    } finally {
      setRunningImport(false);
    }
  };

  const fetchJobDetails = async (jobId) => {
    try {
      const response = await serviceApi.getImportJobById(jobId);
      setSelectedJob(response.data);
      
      // If job needs review, show the matching modal
      if (response.data.status === 'needs_review' && response.data.result?.conflicts) {
        setConflicts(response.data.result.conflicts);
        setShowMatchingModal(true);
      } else {
        setShowJobModal(true);
      }
    } catch (err) {
      console.error('Error fetching job details:', err);
    }
  };

  const handleResolveConflict = async (importedUser, action) => {
    try {
      setResolvingConflict(true);
      
      await serviceApi.resolveUserConflict(selectedJob.id, importedUser, action);
      
      // Remove the resolved conflict from the list
      setConflicts(conflicts.filter(conflict => 
        conflict.username !== importedUser.username || 
        conflict.service_id !== importedUser.service_id
      ));
      
      // If no more conflicts, close the modal and refresh jobs
      if (conflicts.length <= 1) {
        setShowMatchingModal(false);
        fetchImportJobs();
      }
    } catch (err) {
      console.error('Error resolving conflict:', err);
    } finally {
      setResolvingConflict(false);
    }
  };

  const handleScheduleImport = (service) => {
    setSelectedService(service);
    setScheduleForm({
      interval_seconds: 86400, // Default to daily
      start_time: null
    });
    setShowScheduleModal(true);
  };

  const handleScheduleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSavingSchedule(true);
      
      await serviceApi.scheduleImport(selectedService.id, scheduleForm);
      
      setShowScheduleModal(false);
      
      // Show success message
      toast.success(t('services:scheduleSuccess'));
    } catch (err) {
      toast.error(t('services:scheduleFailed'));
      console.error('Error scheduling import:', err);
    } finally {
      setSavingSchedule(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="secondary">Pending</Badge>;
      case 'running':
        return <Badge bg="primary">Running</Badge>;
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      case 'needs_review':
        return <Badge bg="warning">Needs Review</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return 'N/A';
    return formatTimestamp(dateTimeStr);
  };

  const formatInterval = (seconds) => {
    if (seconds === 86400) return 'Daily';
    if (seconds === 604800) return 'Weekly';
    if (seconds === 2592000) return 'Monthly';
    
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    let result = '';
    if (days > 0) result += `${days} day${days > 1 ? 's' : ''} `;
    if (hours > 0) result += `${hours} hour${hours > 1 ? 's' : ''} `;
    if (minutes > 0) result += `${minutes} minute${minutes > 1 ? 's' : ''} `;
    
    return result.trim() || `${seconds} seconds`;
  };

  if (loading && services.length === 0) {
    return (
      <div className="d-flex justify-content-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="services-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-cloud me-2"></i>
          {t('services:title')}
        </h1>
        {hasPermission('services', 'create') && (
          <Button variant="primary" onClick={handleCreateService}>
            <i className="fas fa-plus me-2"></i>
            {t('services:addService')}
          </Button>
        )}
      </div>

      {error && (
        <Alert variant={error.includes('success') ? 'success' : 'danger'} onClose={() => setError(null)} dismissible>
          <i className={`fas fa-${error.includes('success') ? 'check-circle' : 'exclamation-circle'} me-2`}></i>
          {error}
        </Alert>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-4"
      >
        <Tab eventKey="services" title={<><i className="fas fa-cloud me-2"></i>{t('services:tabs.services')}</>}>
          {services.length === 0 ? (
            <Card className="shadow-sm mb-4">
              <Card.Body className="text-center py-5">
                <i className="fas fa-cloud text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                <h4 className="text-muted">{t('services:noServices')}</h4>
                <p className="text-muted mb-4">
                  {t('services:noServicesMessage')}
                </p>
                {hasPermission('services', 'create') && (
                  <Button variant="primary" onClick={handleCreateService}>
                    <i className="fas fa-plus me-2"></i>
                    {t('services:addService')}
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : (
            <div className="row">
              {services.map(service => (
                <div key={service.id} className="col-md-6 col-lg-4 mb-4">
                  <Card className="shadow-sm h-100">
                    <Card.Header className="bg-primary text-white">
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                          <i className="fas fa-cloud me-2"></i>
                          {service.name}
                        </h5>
                        <div className="dropdown">
                          <Button variant="link" className="text-white p-0" data-bs-toggle="dropdown">
                            <i className="fas fa-ellipsis-v"></i>
                          </Button>
                          <ul className="dropdown-menu dropdown-menu-end">
                            {hasPermission('services', 'update') && (
                              <li>
                                <button className="dropdown-item" onClick={() => handleEditService(service)}>
                                  <i className="fas fa-edit me-2"></i>
                                  {t('services:actions.edit')}
                                </button>
                              </li>
                            )}
                            {hasPermission('services', 'delete') && (
                              <li>
                                <button className="dropdown-item text-danger" onClick={() => handleDeleteService(service.id)}>
                                  <i className="fas fa-trash me-2"></i>
                                  {t('services:actions.delete')}
                                </button>
                              </li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="mb-3">
                        <small className="text-muted">Endpoint:</small>
                        <div className="text-truncate">
                          <code>{service.endpoint}</code>
                        </div>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">Authentication:</small>
                        <div>
                          {service.apikey ? (
                            <>
                              <code>********</code>
                              {service.auth_method === 'bearer' && (
                                <span className="ms-2 badge bg-secondary">Bearer Token</span>
                              )}
                              {service.auth_method === 'header' && (
                                <span className="ms-2 badge bg-secondary">Header: {service.auth_header_name}</span>
                              )}
                              {service.auth_method === 'query_param' && (
                                <span className="ms-2 badge bg-secondary">Query Param: {service.auth_param_name}</span>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">None</span>
                          )}
                        </div>
                      </div>
                      <div className="mb-3">
                        <small className="text-muted">User Fields:</small>
                        <div>
                          <code>
                            {JSON.stringify(service.user_fields)}
                          </code>
                        </div>
                      </div>
                    </Card.Body>
                    <Card.Footer className="bg-light">
                      <div className="d-flex justify-content-between">
                        {hasPermission('services', 'import') && (
                          <>
                            <div className="btn-group">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleRunImport(service.id, 'dry-run')}
                                disabled={runningImport}
                              >
                                <i className="fas fa-vial me-1"></i>
                                {t('services:actions.dryRun')}
                              </Button>
                              <Button 
                                variant="outline-success" 
                                size="sm"
                                onClick={() => handleRunImport(service.id, 'run')}
                                disabled={runningImport}
                              >
                                <i className="fas fa-play me-1"></i>
                                {t('services:actions.run')}
                              </Button>
                            </div>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => handleScheduleImport(service)}
                              disabled={runningImport}
                            >
                              <i className="fas fa-clock me-1"></i>
                              {t('services:actions.schedule')}
                            </Button>
                          </>
                        )}
                      </div>
                    </Card.Footer>
                  </Card>
                </div>
              ))}
            </div>
          )}
        </Tab>
        <Tab eventKey="jobs" title={<><i className="fas fa-history me-2"></i>{t('services:tabs.jobs')}</>}>
          <Card className="shadow-sm">
            <Card.Body>
              {loadingJobs ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : importJobs.length === 0 ? (
                <div className="text-center py-4">
                  <i className="fas fa-history text-muted mb-3" style={{ fontSize: '3rem' }}></i>
                  <h4 className="text-muted">{t('services:import.noJobs')}</h4>
                  <p className="text-muted">
                    {t('services:import.noJobsMessage')}
                  </p>
                </div>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Service</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Started</th>
                      <th>Finished</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...importJobs]
                      .sort((a, b) => new Date(b.started_at) - new Date(a.started_at))
                      .map(job => {
                      // Find the service name
                      const service = services.find(s => s.id === job.service_id);
                      
                      return (
                        <tr key={job.id}>
                          <td>{service?.name || job.service_id}</td>
                          <td>
                            {job.run_type === 'dry-run' ? (
                              <Badge bg="info">Dry Run</Badge>
                            ) : job.run_type === 'scheduled' ? (
                              <Badge bg="secondary">Scheduled</Badge>
                            ) : (
                              <Badge bg="primary">Run</Badge>
                            )}
                          </td>
                          <td>{getStatusBadge(job.status)}</td>
                          <td>{formatDateTime(job.started_at)}</td>
                          <td>{job.finished_at ? formatDateTime(job.finished_at) : 'In Progress'}</td>
                          <td>
                            <Button 
                              variant="outline-secondary" 
                              size="sm"
                              onClick={() => fetchJobDetails(job.id)}
                            >
                              <i className="fas fa-eye me-1"></i>
                              {t('services:actions.details')}
                            </Button>
                            {job.status === 'needs_review' && (
                              <Button 
                                variant="outline-warning" 
                                size="sm"
                                className="ms-2"
                                onClick={() => fetchJobDetails(job.id)}
                              >
                              <i className="fas fa-exclamation-triangle me-1"></i>
                              {t('services:actions.review')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Service Modal */}
      <Modal show={showServiceModal} onHide={() => setShowServiceModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingService ? (
              <><i className="fas fa-edit me-2"></i>Edit Service</>
            ) : (
              <><i className="fas fa-plus me-2"></i>Add Service</>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {serviceFormError && (
            <Alert variant="danger">
              <i className="fas fa-exclamation-circle me-2"></i>
              {serviceFormError}
            </Alert>
          )}
          
          <Form onSubmit={handleServiceFormSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Service Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={serviceForm.name}
                onChange={handleServiceFormChange}
                required
                placeholder="Enter a name for this service"
              />
              <Form.Text className="text-muted">
                A descriptive name for the service.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>API Endpoint</Form.Label>
              <Form.Control
                type="url"
                name="endpoint"
                value={serviceForm.endpoint}
                onChange={handleServiceFormChange}
                required
                placeholder="https://api.example.com/users"
              />
              <Form.Text className="text-muted">
                The URL endpoint that returns user data.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>API Key (Optional)</Form.Label>
              <Form.Control
                type="password"
                name="apikey"
                value={serviceForm.apikey}
                onChange={handleServiceFormChange}
                placeholder="Enter API key if required"
                autoComplete="new-password"
              />
              <Form.Text className="text-muted">
                If the API requires authentication, enter the API key here.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Authentication Method</Form.Label>
              <Form.Select
                name="auth_method"
                value={serviceForm.auth_method}
                onChange={handleServiceFormChange}
              >
                <option value="bearer">Bearer Token</option>
                <option value="header">Custom Header</option>
                <option value="query_param">Query Parameter</option>
              </Form.Select>
              <Form.Text className="text-muted">
                How the API key should be used in requests.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Response Format</Form.Label>
              <Form.Select
                name="response_format"
                value={serviceForm.response_format}
                onChange={handleServiceFormChange}
              >
                <option value="json">JSON</option>
                <option value="xml">XML</option>
              </Form.Select>
              <Form.Text className="text-muted">
                The format of the API response.
              </Form.Text>
            </Form.Group>
            
            {serviceForm.auth_method === 'header' && (
              <Form.Group className="mb-3">
                <Form.Label>Header Name</Form.Label>
                <Form.Control
                  type="text"
                  name="auth_header_name"
                  value={serviceForm.auth_header_name}
                  onChange={handleServiceFormChange}
                  placeholder="e.g., X-API-Key, X-Emby-Token"
                  required
                />
                <Form.Text className="text-muted">
                  The name of the header to use for authentication.
                </Form.Text>
              </Form.Group>
            )}
            
            {serviceForm.auth_method === 'query_param' && (
              <Form.Group className="mb-3">
                <Form.Label>Query Parameter Name</Form.Label>
                <Form.Control
                  type="text"
                  name="auth_param_name"
                  value={serviceForm.auth_param_name}
                  onChange={handleServiceFormChange}
                  placeholder="e.g., api_key, token"
                  required
                />
                <Form.Text className="text-muted">
                  The name of the query parameter to use for authentication.
                </Form.Text>
              </Form.Group>
            )}
            
            <hr />
            
            <h5>User Field Mapping</h5>
            <p className="text-muted small">
              Map the fields from the API response to the user fields in the system.
            </p>
            
            <Form.Group className="mb-3">
              <Form.Label>Username Field</Form.Label>
              <Form.Control
                type="text"
                name="user_fields.username"
                value={serviceForm.user_fields.username}
                onChange={handleServiceFormChange}
                required
                placeholder="e.g., username, login, name"
              />
              <Form.Text className="text-muted">
                The field in the API response that contains the username.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email Field (Optional)</Form.Label>
              <Form.Control
                type="text"
                name="user_fields.email"
                value={serviceForm.user_fields.email}
                onChange={handleServiceFormChange}
                placeholder="e.g., email, mail, email_address"
              />
              <Form.Text className="text-muted">
                The field in the API response that contains the email address.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowServiceModal(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleServiceFormSubmit}
            disabled={savingService}
          >
            {savingService ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-2"></i>
                Save Service
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Job Details Modal */}
      {selectedJob && (
        <Modal show={showJobModal} onHide={() => setShowJobModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-info-circle me-2"></i>
              Import Job Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-4">
              <div className="d-flex justify-content-between mb-2">
                <h5>Job Information</h5>
                {getStatusBadge(selectedJob.status)}
              </div>
              
              <Table bordered>
                <tbody>
                  <tr>
                    <th style={{ width: '30%' }}>Service</th>
                    <td>{services.find(s => s.id === selectedJob.service_id)?.name || selectedJob.service_id}</td>
                  </tr>
                  <tr>
                    <th>Type</th>
                    <td>
                      {selectedJob.run_type === 'dry-run' ? 'Dry Run' : 
                       selectedJob.run_type === 'scheduled' ? 'Scheduled' : 'Run'}
                    </td>
                  </tr>
                  <tr>
                    <th>Started</th>
                    <td>{formatDateTime(selectedJob.started_at)}</td>
                  </tr>
                  <tr>
                    <th>Finished</th>
                    <td>{selectedJob.finished_at ? formatDateTime(selectedJob.finished_at) : 'In Progress'}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
            
            {selectedJob.result && (
              <div className="mb-4">
                <h5>Results</h5>
                
                <Table bordered>
                  <tbody>
                    <tr>
                      <th style={{ width: '30%' }}>Total Users</th>
                      <td>{selectedJob.result.total_users || 0}</td>
                    </tr>
                    <tr>
                      <th>Matched Users</th>
                      <td>{selectedJob.result.matched_users?.length || 0}</td>
                    </tr>
                    <tr>
                      <th>Conflicts</th>
                      <td>{selectedJob.result.conflicts?.length || 0}</td>
                    </tr>
                    {selectedJob.result.imported_count !== undefined && (
                      <tr>
                        <th>Imported Users</th>
                        <td>{selectedJob.result.imported_count}</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                
                {selectedJob.result.error && (
                  <Alert variant="danger">
                    <i className="fas fa-exclamation-circle me-2"></i>
                    Error: {selectedJob.result.error}
                  </Alert>
                )}
                
                {selectedJob.status === 'needs_review' && (
                  <Alert variant="warning">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    This import job requires manual review to resolve user conflicts.
                    <div className="mt-2">
                      <Button 
                        variant="warning" 
                        onClick={() => {
                          setConflicts(selectedJob.result.conflicts || []);
                          setShowJobModal(false);
                          setShowMatchingModal(true);
                        }}
                      >
                        <i className="fas fa-user-check me-2"></i>
                        Review Conflicts
                      </Button>
                    </div>
                  </Alert>
                )}
                
                {selectedJob.result.matched_users && selectedJob.result.matched_users.length > 0 && (
                  <Accordion className="mt-4">
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        <i className="fas fa-users me-2"></i>
                        User Matches ({selectedJob.result.matched_users.length})
                      </Accordion.Header>
                      <Accordion.Body style={{ maxHeight: '300px', overflow: 'auto' }}>
                        <Table hover>
                          <thead>
                            <tr>
                              <th>Username</th>
                              <th>Email</th>
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedJob.result.matched_users.map((match, index) => (
                              <tr key={index}>
                                <td>{match.imported_user.username}</td>
                                <td>{match.imported_user.email || 'N/A'}</td>
                                <td>
                                  {match.action === 'create' ? (
                                    <Badge bg="success">Create New</Badge>
                                  ) : match.action === 'merge' ? (
                                    <Badge bg="primary">Merge</Badge>
                                  ) : (
                                    <Badge bg="secondary">{match.action}</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}
                
                {selectedJob.result.conflicts && selectedJob.result.conflicts.length > 0 && (
                  <Accordion className="mt-3">
                    <Accordion.Item eventKey="1">
                      <Accordion.Header>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Conflicts ({selectedJob.result.conflicts.length})
                      </Accordion.Header>
                      <Accordion.Body style={{ maxHeight: '300px', overflow: 'auto' }}>
                        <Table hover>
                          <thead>
                            <tr>
                              <th>Username</th>
                              <th>Email</th>
                              <th>Match Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedJob.result.conflicts.map((conflict, index) => (
                              <tr key={index}>
                                <td>{conflict.username}</td>
                                <td>{conflict.email || 'N/A'}</td>
                                <td>{conflict.match_status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowJobModal(false)}>
            {t('common:actions.close')}
          </Button>
            {selectedJob.status === 'needs_review' && (
              <Button 
                variant="warning" 
                onClick={() => {
                  setConflicts(selectedJob.result.conflicts || []);
                  setShowJobModal(false);
                  setShowMatchingModal(true);
                }}
              >
                <i className="fas fa-user-check me-2"></i>
                Review Conflicts
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {/* User Matching Modal */}
      <Modal show={showMatchingModal} onHide={() => setShowMatchingModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-user-check me-2"></i>
            Resolve User Conflicts
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info">
            <i className="fas fa-info-circle me-2"></i>
            These users need manual review because they have potential conflicts with existing users.
            Please decide how to handle each user.
          </Alert>
          
          {conflicts.length === 0 ? (
            <div className="text-center py-4">
              <i className="fas fa-check-circle text-success mb-3" style={{ fontSize: '3rem' }}></i>
              <h4>All conflicts resolved!</h4>
              <p className="text-muted">
                There are no more conflicts to resolve.
              </p>
            </div>
          ) : (
            <div className="user-conflicts">
              {conflicts.map((conflict, index) => (
                <Card key={index} className="mb-4 shadow-sm">
                  <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      User Conflict: {conflict.username}
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="row">
                      <div className="col-md-6">
                        <h6>Imported User</h6>
                        <Table bordered>
                          <tbody>
                            <tr>
                              <th style={{ width: '30%' }}>Username</th>
                              <td>{conflict.username}</td>
                            </tr>
                            <tr>
                              <th>Email</th>
                              <td>{conflict.email || 'N/A'}</td>
                            </tr>
                            <tr>
                              <th>Service</th>
                              <td>{services.find(s => s.id === conflict.service_id)?.name || conflict.service_id}</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                      
                      <div className="col-md-6">
                        {conflict.matched_user_id && (
                          <>
                            <h6>Potential Match</h6>
                            <div className="alert alert-warning">
                              <i className="fas fa-exclamation-triangle me-2"></i>
                              This user might match an existing user in the system.
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <h6>Resolution</h6>
                      <div className="d-flex gap-2">
                        {conflict.matched_user_id && (
                          <Button 
                            variant="primary" 
                            onClick={() => handleResolveConflict(conflict, 'merge')}
                            disabled={resolvingConflict}
                          >
                            <i className="fas fa-link me-2"></i>
                            Merge with Existing User
                          </Button>
                        )}
                        
                        <Button 
                          variant="success" 
                          onClick={() => handleResolveConflict(conflict, 'create')}
                          disabled={resolvingConflict}
                        >
                          <i className="fas fa-user-plus me-2"></i>
                          Create New User
                        </Button>
                        
                        <Button 
                          variant="secondary" 
                          onClick={() => handleResolveConflict(conflict, 'skip')}
                          disabled={resolvingConflict}
                        >
                          <i className="fas fa-ban me-2"></i>
                          Skip
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMatchingModal(false)}>
            {t('common:actions.close')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Schedule Modal */}
      <Modal show={showScheduleModal} onHide={() => setShowScheduleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-clock me-2"></i>
            Schedule Import
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedService && (
            <Form onSubmit={handleScheduleFormSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Service</Form.Label>
                <Form.Control
                  type="text"
                  value={selectedService.name}
                  disabled
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Frequency</Form.Label>
                <Form.Select
                  name="interval_seconds"
                  value={scheduleForm.interval_seconds}
                  onChange={(e) => setScheduleForm({
                    ...scheduleForm,
                    interval_seconds: parseInt(e.target.value)
                  })}
                >
                  <option value="86400">Daily</option>
                  <option value="604800">Weekly</option>
                  <option value="2592000">Monthly</option>
                  <option value="3600">Hourly</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  How often the import should run.
                </Form.Text>
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Start Time (Optional)</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="start_time"
                  value={scheduleForm.start_time || ''}
                  onChange={(e) => setScheduleForm({
                    ...scheduleForm,
                    start_time: e.target.value || null
                  })}
                />
                <Form.Text className="text-muted">
                  When the first import should run. If not specified, it will run immediately.
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowScheduleModal(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={handleScheduleFormSubmit}
            disabled={savingSchedule}
          >
            {savingSchedule ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Scheduling...
              </>
            ) : (
              <>
                <i className="fas fa-clock me-2"></i>
                Schedule Import
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Services;
